import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  ColumnFilterAmount,
  ColumnFilterEmpty,
  ColumnFilterSelect,
  ColumnFilterText,
  toAmount,
} from "../../faturas/ColumnFilters";
import {
  Add20Regular,
  ArrowUndo16Regular,
  CheckmarkCircle16Regular,
  Delete16Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  FolhaHorasMobileForm,
  type MobileTimesheetValues,
  type RecentTimesheetItem,
} from "./FolhaHorasMobileForm";
import { useAuth } from "../../auth/AuthContext";
import {
  createTimesheetEntry,
  deleteTimesheetEntry,
  getMyWorker,
  getTimesheet,
  getTimesheetWorkers,
  validateTimesheetEntry,
  type TimesheetEntry,
} from "../../folhaDeHoras/folhaDeHorasApi";
import { type Worker } from "../../trabalhadores/trabalhadoresApi";
import { getWorkOptions, type WorkOption } from "../../obras/obrasApi";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../components/SearchableSelect";

const FREE_LOCATION = "__free__";

const toInput = (date: Date): string => date.toISOString().slice(0, 10);
const todayInput = (): string => toInput(new Date());
const firstOfMonthInput = (): string => {
  const now = new Date();
  return toInput(new Date(now.getFullYear(), now.getMonth(), 1));
};

// Parse a localized/plain number string into a finite number, or null when blank/invalid.
const parseNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const normalized = trimmed.replace(/\s/g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatHours = (value: number): string => {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatDate = (iso: string): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("pt-PT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export function FolhaDeHorasView() {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes("Administrator") ?? false;

  const [myWorker, setMyWorker] = useState<Worker | null>(null);
  const [contextLoading, setContextLoading] = useState(true);
  // Trabalhadores geríveis: admin → todos; encarregado → ele próprio + equipa.
  const [manageableWorkers, setManageableWorkers] = useState<Worker[]>([]);
  const [works, setWorks] = useState<WorkOption[]>([]);
  // Separado do `error` das horas: aquele é limpo a cada recarga da lista e apagava este.
  const [contextError, setContextError] = useState<string | null>(null);

  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [validatingId, setValidatingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  // Which worker we are viewing/logging for ("" = todo o âmbito).
  const [selectedWorkerId, setSelectedWorkerId] = useState("");

  // Date range filter.
  const [from, setFrom] = useState(firstOfMonthInput());
  const [to, setTo] = useState(todayInput());

  // Filtros por coluna (estilo Excel, como nas faturas). Aqui filtram no CLIENTE: a folha já
  // carrega o período inteiro de uma vez (sem paginação) e o total soma-se da lista visível,
  // por isso filtrar em memória é instantâneo e o rodapé acompanha por construção.
  const [workerFilter, setWorkerFilter] = useState("");
  const [localFilter, setLocalFilter] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("");
  const [horasFilter, setHorasFilter] = useState("");
  const [descritivoFilter, setDescritivoFilter] = useState("");

  // Add form fields.
  const [data, setData] = useState(todayInput());
  const [workSel, setWorkSel] = useState("");
  const [localTexto, setLocalTexto] = useState("");
  const [horas, setHoras] = useState("");
  const [descritivo, setDescritivo] = useState("");

  const isSupervisor = myWorker?.isSupervisor ?? false;
  const myWorkerId = myWorker?.id ?? null;
  // Quem pode lançar/validar horas de outros: Administrador ou Encarregado de Obra.
  const canManageOthers = isAdmin || isSupervisor;

  // Load the self-service context: which worker (if any) is linked to this account,
  // the obra list, and the workers this user may log/validate for.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // allSettled e não all: com `all`, uma única chamada falhada descartava também as
      // respostas boas das outras duas, e a página ficava sem trabalhadores, sem obras e sem
      // formulário — em silêncio, porque o catch não mostrava nada.
      const [worker, workList, manageable] = await Promise.allSettled([
        getMyWorker(),
        getWorkOptions(),
        getTimesheetWorkers(),
      ]);
      if (cancelled) return;

      if (worker.status === "fulfilled") setMyWorker(worker.value);
      if (workList.status === "fulfilled") setWorks(workList.value);
      if (manageable.status === "fulfilled") setManageableWorkers(manageable.value);

      const falhadas = [
        workList.status === "rejected" ? "obras" : null,
        manageable.status === "rejected" ? "trabalhadores" : null,
      ].filter((nome): nome is string => nome !== null);

      setContextError(
        falhadas.length > 0
          ? `Não foi possível carregar ${falhadas.join(" e ")}. Recarregue a página; ` +
            "se persistir, contacte o suporte."
          : null,
      );
      setContextLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadEntries = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const list = await getTimesheet({
        workerId:
          canManageOthers && selectedWorkerId ? Number(selectedWorkerId) : undefined,
        from: from || undefined,
        to: to || undefined,
      });
      setEntries(list);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível carregar as horas.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [canManageOthers, selectedWorkerId, from, to]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const activeWorks = useMemo(
    () => works.slice().sort((a, b) => a.name.localeCompare(b.name, "pt-PT")),
    [works],
  );

  const selectableWorkers = useMemo(
    () =>
      manageableWorkers
        .filter((worker) => worker.isActive)
        .sort((a, b) => a.name.localeCompare(b.name, "pt-PT")),
    [manageableWorkers],
  );

  const workerSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      {
        value: "",
        label: isAdmin ? "Todos os trabalhadores" : "Toda a minha equipa",
      },
      ...selectableWorkers.map((worker) => ({
        value: String(worker.id),
        label: `${worker.name}${worker.code ? ` (${worker.code})` : ""}${
          worker.id === myWorkerId ? " — eu" : ""
        }`,
        keywords: worker.code ?? undefined,
      })),
    ],
    [selectableWorkers, isAdmin, myWorkerId],
  );

  const workSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      ...activeWorks.map((work) => ({
        value: String(work.id),
        label: `${work.name}${work.code ? ` (${work.code})` : ""}`,
        keywords: work.code,
      })),
      { value: FREE_LOCATION, label: "Outro (local livre)…" },
    ],
    [activeWorks],
  );

  const workerFilterOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: "", label: "Todos" },
      ...Array.from(new Set(entries.map((entry) => entry.workerName).filter(Boolean)))
        .sort((a, b) => a.localeCompare(b, "pt-PT"))
        .map((name) => ({ value: name, label: name })),
    ],
    [entries],
  );

  const localFilterOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: "", label: "Todos" },
      ...Array.from(new Set(entries.map((entry) => entry.locationLabel).filter(Boolean)))
        .sort((a, b) => a.localeCompare(b, "pt-PT"))
        .map((label) => ({ value: label, label })),
    ],
    [entries],
  );

  const filteredEntries = useMemo(() => {
    const term = descritivoFilter.trim().toLowerCase();
    const horas = toAmount(horasFilter);
    return entries.filter(
      (entry) =>
        (!workerFilter || entry.workerName === workerFilter) &&
        (!localFilter || entry.locationLabel === localFilter) &&
        (!estadoFilter || (estadoFilter === "validada") === entry.isValidated) &&
        (horas === null || entry.hours === horas) &&
        (term === "" || (entry.notes ?? "").toLowerCase().includes(term)),
    );
  }, [entries, workerFilter, localFilter, estadoFilter, horasFilter, descritivoFilter]);

  const hasColumnFilters = Boolean(
    workerFilter || localFilter || estadoFilter || horasFilter || descritivoFilter,
  );

  const clearColumnFilters = () => {
    setWorkerFilter("");
    setLocalFilter("");
    setEstadoFilter("");
    setHorasFilter("");
    setDescritivoFilter("");
  };

  const totalHoras = useMemo(
    () => filteredEntries.reduce((sum, entry) => sum + (entry.hours ?? 0), 0),
    [filteredEntries],
  );

  // Lista compacta do assistente mobile — ~2 semanas de registos chegam para conferir no local.
  const recentEntries = useMemo<RecentTimesheetItem[]>(
    () =>
      entries.slice(0, 14).map((entry) => ({
        id: entry.id,
        title: entry.locationLabel || "—",
        subtitle: `${formatDate(entry.workedOn)}${
          canManageOthers && entry.workerName ? ` · ${entry.workerName}` : ""
        }`,
        hoursLabel: `${formatHours(entry.hours)} h`,
        validated: entry.isValidated,
      })),
    [entries, canManageOthers],
  );

  // Whether the current user can log hours: a linked worker (self-service) or a
  // manager (admin/encarregado) who has picked a specific worker.
  const canLog = canManageOthers ? selectedWorkerId !== "" : myWorker !== null;
  const notLinked = !contextLoading && !canManageOthers && myWorker === null;

  // Só o Admin (qualquer registo) ou o Encarregado (registos da sua equipa, não os
  // próprios) pode validar/reverter horas.
  const canValidateRow = (entry: TimesheetEntry): boolean => {
    if (isAdmin) return true;
    return isSupervisor && entry.workerId != null && entry.workerId !== myWorkerId;
  };

  // O próprio não pode apagar horas já validadas (bloqueio). O encarregado pode
  // gerir os registos da equipa; o Admin pode tudo.
  const canDeleteRow = (entry: TimesheetEntry): boolean => {
    if (isAdmin) return true;
    if (entry.workerId != null && entry.workerId === myWorkerId) {
      return !entry.isValidated;
    }
    return isSupervisor && entry.workerId != null && entry.workerId !== myWorkerId;
  };

  const resetForm = () => {
    setData(todayInput());
    setWorkSel("");
    setLocalTexto("");
    setHoras("");
    setDescritivo("");
  };

  const handleAdd = async () => {
    if (isSaving) return;

    if (!data) {
      setError("A data é obrigatória.");
      return;
    }
    const hours = parseNumber(horas);
    if (hours === null || hours <= 0) {
      setError("Indique um número de horas válido (> 0).");
      return;
    }

    let workId: number | null = null;
    let locationText: string | null = null;
    if (workSel === FREE_LOCATION) {
      const local = localTexto.trim();
      if (local === "") {
        setError("Escreva o local ou escolha uma obra.");
        return;
      }
      locationText = local;
    } else if (workSel !== "") {
      workId = Number(workSel);
    } else {
      setError("Escolha uma obra ou um local.");
      return;
    }

    if (canManageOthers && !selectedWorkerId) {
      setError("Selecione um trabalhador para lançar as horas.");
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const created = await createTimesheetEntry({
        workerId:
          canManageOthers && selectedWorkerId ? Number(selectedWorkerId) : undefined,
        workId,
        locationText,
        workedOn: data,
        hours,
        notes: descritivo.trim() !== "" ? descritivo.trim() : null,
      });
      // Keep the list sorted by date desc; a simple reload is the safest.
      setEntries((prev) =>
        [created, ...prev].sort((a, b) => b.workedOn.localeCompare(a.workedOn)),
      );
      resetForm();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível adicionar as horas.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // O assistente do telefone entrega os valores já decididos; o resto é igual ao handleAdd.
  const handleMobileSubmit = async (values: MobileTimesheetValues): Promise<boolean> => {
    if (isSaving) return false;
    setIsSaving(true);
    setError(null);
    try {
      const created = await createTimesheetEntry({
        workerId:
          canManageOthers && selectedWorkerId ? Number(selectedWorkerId) : undefined,
        workId: values.workId,
        locationText: values.locationText,
        workedOn: values.workedOn,
        hours: values.hours,
        notes: values.notes,
      });
      setEntries((prev) =>
        [created, ...prev].sort((a, b) => b.workedOn.localeCompare(a.workedOn)),
      );
      return true;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível adicionar as horas.",
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleValidate = async (entry: TimesheetEntry) => {
    if (validatingId !== null) return;
    setValidatingId(entry.id);
    setError(null);
    try {
      const updated = await validateTimesheetEntry(entry.id, !entry.isValidated);
      setEntries((prev) => prev.map((item) => (item.id === entry.id ? updated : item)));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível validar as horas.",
      );
    } finally {
      setValidatingId(null);
    }
  };

  const handleDelete = async (entry: TimesheetEntry) => {
    if (confirmDeleteId !== entry.id) {
      setConfirmDeleteId(entry.id);
      return;
    }
    setDeletingId(entry.id);
    setError(null);
    try {
      await deleteTimesheetEntry(entry.id);
      setEntries((prev) => prev.filter((item) => item.id !== entry.id));
    } catch (err) {
      // A 404 means it is already gone — treat as success.
      if (err instanceof Error && err.message.includes("404")) {
        setEntries((prev) => prev.filter((item) => item.id !== entry.id));
      } else {
        setError(
          err instanceof Error ? err.message : "Não foi possível eliminar as horas.",
        );
      }
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const inputClass =
    "px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary";

  const colCount = 6 + (canManageOthers ? 1 : 0);

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Folha de Horas"
        subtitle="Registe as horas trabalhadas por dia e por obra."
      />
      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto">
          {error && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {error}
            </div>
          )}

          {contextError && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {contextError}
            </div>
          )}

          {notLinked && (
            <div
              className="border border-border bg-muted px-4 py-3 text-sm text-secondary-text"
              style={{ borderRadius: "var(--radius)" }}
            >
              A sua conta ainda não está associada a um trabalhador. Contacte um
              administrador para poder registar as suas horas.
            </div>
          )}

          {/* No telefone: assistente de passos com botões grandes; o desktop mantém o
              formulário e a tabela clássicos (hidden md:block abaixo). */}
          <div className="md:hidden">
            {contextLoading && (
              <p className="text-sm text-secondary-text">A carregar…</p>
            )}
            {!contextLoading && !notLinked && (
              <FolhaHorasMobileForm
                canManageOthers={canManageOthers}
                workers={selectableWorkers}
                selectedWorkerId={selectedWorkerId}
                onSelectWorker={setSelectedWorkerId}
                works={activeWorks}
                recent={recentEntries}
                isSaving={isSaving}
                onSubmit={handleMobileSubmit}
              />
            )}
          </div>

          <div className="hidden md:block space-y-6">
          {/* Admin / Encarregado: escolher o trabalhador para ver / lançar horas. */}
          {canManageOthers && (
            <div
              className="bg-card border border-border p-4"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <SearchableSelect<string>
                style={{ maxWidth: "24rem" }}
                label="Trabalhador"
                labelClassName="text-sm text-secondary-text"
                labelStyle={{}}
                value={selectedWorkerId}
                onChange={(next) => setSelectedWorkerId(next ?? "")}
                options={workerSelectOptions}
                allowClear={false}
                searchPlaceholder="Procurar trabalhador…"
              />
              <p className="text-xs text-secondary-text mt-2">
                {isAdmin
                  ? 'Escolha um trabalhador para lançar horas em nome dele. "Todos" mostra o histórico de toda a equipa.'
                  : 'Escolha um trabalhador da sua equipa para lançar ou validar horas. "Toda a minha equipa" mostra tudo o que está por validar.'}
              </p>

              {/* Sem esta linha, uma lista vazia é indistinguível de uma lista por carregar: o
                  seletor mostra só "Todos" e nada explica porque não há ninguém para escolher. */}
              {!contextLoading && selectableWorkers.length === 0 && (
                <p className="text-xs text-destructive mt-1">
                  Não há trabalhadores activos para escolher. Crie um em Trabalhadores (ou
                  reactive um existente) para poder lançar horas.
                </p>
              )}
            </div>
          )}

          {/* Formulário de registo. Também quando ainda não há trabalhador escolhido: o botão
              fica desactivado e a dica lá dentro explica porquê. Escondê-lo aqui tornava essa
              dica inalcançável — quem gere horas de outros abria a página sem formulário, sem
              botão e sem explicação, com a tabela a mandá-lo usar "o formulário acima". */}
          {(canLog || canManageOthers || contextLoading) && (
            <div
              className="bg-card border border-border p-4"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex flex-col gap-1 text-sm text-secondary-text">
                  Data *
                  <input
                    type="date"
                    className={inputClass}
                    value={data}
                    onChange={(event) => setData(event.target.value)}
                  />
                </label>

                <label className="flex flex-col gap-1 text-sm text-secondary-text">
                  Horas *
                  <input
                    type="text"
                    inputMode="decimal"
                    className={inputClass}
                    value={horas}
                    onChange={(event) => setHoras(event.target.value)}
                    placeholder="0"
                  />
                </label>

                <SearchableSelect<string>
                  label="Local / Obra"
                  required
                  labelClassName="text-sm text-secondary-text"
                  labelStyle={{}}
                  value={workSel === "" ? null : workSel}
                  onChange={(next) => setWorkSel(next ?? "")}
                  options={workSelectOptions}
                  placeholder="—"
                  searchPlaceholder="Procurar obra…"
                />

                {workSel === FREE_LOCATION && (
                  <label className="flex flex-col gap-1 text-sm text-secondary-text">
                    Local (texto livre) *
                    <input
                      type="text"
                      className={inputClass}
                      value={localTexto}
                      onChange={(event) => setLocalTexto(event.target.value)}
                      placeholder="ex.: Armazém, fornecedor…"
                    />
                  </label>
                )}

                <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
                  Descritivo do trabalho
                  <textarea
                    className={inputClass}
                    rows={2}
                    value={descritivo}
                    onChange={(event) => setDescritivo(event.target.value)}
                    placeholder="O que foi feito"
                  />
                </label>
              </div>

              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={isSaving || (canManageOthers && !selectedWorkerId)}
                  className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] bg-primary text-primary-foreground disabled:opacity-50"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  <Add20Regular className="w-5 h-5" />
                  {isSaving ? "A guardar…" : "Adicionar"}
                </button>
                {canManageOthers && !selectedWorkerId && (
                  <span className="text-xs text-secondary-text">
                    Selecione um trabalhador acima para lançar horas.
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Filtro de datas. */}
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              De
              <input
                type="date"
                className={inputClass}
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Até
              <input
                type="date"
                className={inputClass}
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </label>
          </div>

          {/* Tabela. */}
          <div
            className="bg-card border border-border overflow-x-auto"
            style={{ borderRadius: "var(--radius-card)" }}
          >
            <table className="w-full text-sm" style={{ minWidth: "820px" }}>
              <thead>
                <tr className="border-b border-border bg-muted text-left text-secondary-text">
                  <th className="px-4 py-3" style={{ width: "120px" }}>
                    Data
                  </th>
                  {canManageOthers && <th className="px-4 py-3">Trabalhador</th>}
                  <th className="px-4 py-3">Local / Obra</th>
                  <th className="px-4 py-3 text-right" style={{ width: "90px" }}>
                    Horas
                  </th>
                  <th className="px-4 py-3" style={{ width: "110px" }}>
                    Estado
                  </th>
                  <th className="px-4 py-3">Descritivo</th>
                  <th className="px-4 py-3" style={{ width: "170px" }} />
                </tr>
                {/* Filtros por coluna. A Data filtra-se pelo período (De/Até) acima da tabela. */}
                <tr className="text-left">
                  <ColumnFilterEmpty />
                  {/* Trabalhador e Local/Obra são as colunas de cardinalidade alta — seletor
                      com pesquisa, no padrão compacto das faturas (tokens; o CSS é pré-compilado). */}
                  {canManageOthers && (
                    <th className="px-3 pb-2 font-normal align-top">
                      <SearchableSelect<string>
                        ariaLabel="Trabalhador"
                        value={workerFilter}
                        onChange={(next) => setWorkerFilter(next ?? "")}
                        options={workerFilterOptions}
                        allowClear={false}
                        searchPlaceholder="Procurar…"
                        panelPosition="fixed"
                        className={workerFilter ? "font-medium" : undefined}
                        style={
                          {
                            minWidth: "6.5rem",
                            "--text-base": "var(--text-caption)",
                            ...(workerFilter ? { "--border": "var(--primary)" } : null),
                          } as CSSProperties
                        }
                      />
                    </th>
                  )}
                  <th className="px-3 pb-2 font-normal align-top">
                    <SearchableSelect<string>
                      ariaLabel="Local / Obra"
                      value={localFilter}
                      onChange={(next) => setLocalFilter(next ?? "")}
                      options={localFilterOptions}
                      allowClear={false}
                      searchPlaceholder="Procurar…"
                      panelPosition="fixed"
                      className={localFilter ? "font-medium" : undefined}
                      style={
                        {
                          minWidth: "6.5rem",
                          "--text-base": "var(--text-caption)",
                          ...(localFilter ? { "--border": "var(--primary)" } : null),
                        } as CSSProperties
                      }
                    />
                  </th>
                  <ColumnFilterAmount
                    value={horasFilter}
                    onChange={setHorasFilter}
                    title="Filtrar pelo número exato de horas (ex.: 8 ou 7,5)."
                  />
                  <ColumnFilterSelect
                    value={estadoFilter}
                    label="Estado"
                    onChange={setEstadoFilter}
                    allLabel="Todos"
                    options={[
                      { value: "pendente", label: "Pendente" },
                      { value: "validada", label: "Validada" },
                    ]}
                  />
                  <ColumnFilterText
                    value={descritivoFilter}
                    onChange={setDescritivoFilter}
                    placeholder="Contém…"
                  />
                  <ColumnFilterEmpty />
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={colCount}
                      className="px-4 py-6 text-center text-secondary-text"
                    >
                      A carregar…
                    </td>
                  </tr>
                ) : filteredEntries.length === 0 ? (
                  <tr>
                    <td
                      colSpan={colCount}
                      className="px-4 py-6 text-center text-secondary-text"
                    >
                      {hasColumnFilters ? (
                        <>
                          Sem horas para os filtros aplicados.{" "}
                          <button
                            type="button"
                            className="text-primary underline"
                            onClick={clearColumnFilters}
                          >
                            Limpar filtros
                          </button>
                        </>
                      ) : (
                        "Sem horas registadas no período. Use o formulário acima para adicionar."
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredEntries.map((entry) => (
                    <tr key={entry.id} className="border-b border-border">
                      <td className="px-4 py-3">{formatDate(entry.workedOn)}</td>
                      {canManageOthers && (
                        <td className="px-4 py-3">{entry.workerName || "—"}</td>
                      )}
                      <td className="px-4 py-3">{entry.locationLabel || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {formatHours(entry.hours)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-xs ${
                            entry.isValidated
                              ? "bg-primary/10 text-primary"
                              : "bg-muted text-secondary-text"
                          }`}
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          {entry.isValidated ? "Validada" : "Pendente"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-secondary-text">
                        {entry.notes || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {canValidateRow(entry) && (
                            <button
                              type="button"
                              onClick={() => handleValidate(entry)}
                              disabled={validatingId === entry.id}
                              className="inline-flex items-center gap-1 text-xs text-primary disabled:opacity-50"
                            >
                              {entry.isValidated ? (
                                <ArrowUndo16Regular className="w-4 h-4" />
                              ) : (
                                <CheckmarkCircle16Regular className="w-4 h-4" />
                              )}
                              {entry.isValidated ? "Reverter" : "Validar"}
                            </button>
                          )}
                          {canDeleteRow(entry) && (
                            <button
                              type="button"
                              onClick={() => handleDelete(entry)}
                              disabled={deletingId === entry.id}
                              className="inline-flex items-center gap-1 text-xs text-destructive disabled:opacity-50"
                            >
                              <Delete16Regular className="w-4 h-4" />
                              {confirmDeleteId === entry.id ? "Confirmar?" : "Apagar"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {entries.length > 0 && (
            <div className="text-sm text-secondary-text">
              Total de horas:{" "}
              <span className="text-foreground font-medium">
                {formatHours(totalHoras)} h
              </span>
              {hasColumnFilters && (
                <>
                  {" "}
                  · {filteredEntries.length} de {entries.length} registos{" "}
                  <button
                    type="button"
                    className="text-primary underline"
                    onClick={clearColumnFilters}
                  >
                    Limpar filtros
                  </button>
                </>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
